import { useIsNarrow } from '@/components/blog/useIsNarrow';
import { useIsDark } from '../../useIsDark';

/**
 * EmailFormatVerdictChart — blog18, Figure 3: the actual bottom-line answer.
 *
 * Chart-type-first: this is a DECISION MATRIX (intent -> recommended format),
 * not a numeric chart — the post's real finding is "it depends on intent,"
 * not a single number, so a bar/line chart would misrepresent the conclusion
 * as a scalar comparison. A recommendation table with a confidence-flagged
 * evidence row underneath makes the verdict scannable without overclaiming
 * a single winner (Rule 36 — every "X vs Y" claim carries its confidence).
 *
 * Data: raisy/projects/blog/assets/blog18/raisy_email_format_deliverability_data.json
 * Every number cited is the same one already in the post body / Sources
 * footer — this component adds no new numeric claim, only the synthesis.
 */

const BRAND = {
  teal: '#0AB3BF',
  tealSoft: 'rgba(10,179,191,0.12)',
  slate: '#334155',
  muted: '#64748B',
  warning: '#F59E0B',
  warningSoft: 'rgba(245,158,11,0.12)',
};

type Row = {
  intent: string;
  example: string;
  format: string;
  why: string;
  confidence: string;
};

const ROWS: Row[] = [
  {
    intent: 'First-touch cold outreach',
    example: 'investor email, sales prospecting',
    format: 'Plain text (or lightly formatted)',
    why: 'Reads as personal correspondence to the engagement-based ML layer; 15–25% higher reply rate in aggregate cold-outreach data',
    confidence: 'Aggregate industry data, not a controlled experiment — directional, not proof for any single send',
  },
  {
    intent: 'Newsletter / product update',
    example: 'recurring subscriber email',
    format: 'Multipart MIME (HTML + plain-text fallback)',
    why: 'Recipients already expect bulk-sender formatting; filters expect both renderings from a declared bulk sender',
    confidence: 'Standard practice per email-authentication guidance, not this post’s own measured comparison',
  },
  {
    intent: 'Any bulk send, either format',
    example: '5,000+ msgs/day to Gmail/Yahoo',
    format: 'SPF + DKIM + DMARC required regardless of format',
    why: 'Authentication is checked before content — an unauthenticated plain-text email fails the same gate as HTML',
    confidence: 'Official Google sender guidelines, effective 2024-02-01 — not in dispute',
  },
];

export const EmailFormatVerdictChart = ({ dark }: { dark?: boolean }) => {
  const narrow = useIsNarrow();
  const liveDark = useIsDark();
  const isDark = dark ?? liveDark;

  const C = isDark
    ? { bg: '#0F172A', card: 'bg-slate-900', border: 'border-slate-800', text: '#E2E8F0', muted: '#94A3B8', caption: 'text-slate-400' }
    : { bg: '#FFFFFF', card: 'bg-white', border: 'border-slate-200', text: '#334155', muted: '#64748B', caption: 'text-slate-500' };

  return (
    <div className={`rounded-xl border ${C.border} ${C.card} p-4 sm:p-5`} style={{ backgroundColor: C.bg }}>
      <div className="mb-3 flex items-center gap-2">
        <span
          className="inline-block px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide"
          style={{ backgroundColor: BRAND.tealSoft, color: BRAND.teal }}
        >
          Bottom line
        </span>
        <span className="text-sm" style={{ color: C.muted }}>
          There is no universal winner — the format follows the intent
        </span>
      </div>

      <div className="space-y-3">
        {ROWS.map((row) => (
          <div
            key={row.intent}
            className="rounded-lg p-3 sm:p-4"
            style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC' }}
          >
            <div className={`flex ${narrow ? 'flex-col gap-1.5' : 'items-baseline justify-between gap-4'}`}>
              <div>
                <div className="text-sm font-bold" style={{ color: C.text }}>
                  {row.intent}
                </div>
                <div className="text-xs" style={{ color: C.muted }}>
                  {row.example}
                </div>
              </div>
              <div
                className="text-sm font-bold px-2.5 py-1 rounded-md inline-block w-fit"
                style={{ backgroundColor: BRAND.tealSoft, color: BRAND.teal }}
              >
                {row.format}
              </div>
            </div>
            <p className="text-xs mt-2 leading-relaxed" style={{ color: C.muted }}>
              {row.why}
            </p>
            <p
              className="text-label-xs mt-1.5 leading-relaxed italic border-l-2 pl-2"
              style={{ color: C.muted, borderColor: BRAND.warning }}
            >
              Confidence: {row.confidence}
            </p>
          </div>
        ))}
      </div>

      <p className={`mt-3 text-xs ${C.caption}`}>
        Every figure here restates a number already sourced in the post body and Sources footer — this table adds synthesis, not new data.
      </p>
    </div>
  );
};
